// SceneColorGrade — per-scene color grading + cinematic chapter handoff.
// Lives in the DOM (outside the R3F Canvas) inside the .planet-scene
// wrapper, so it can:
//
//   1) Continuously blend chapter colors near scene boundaries so the
//      field never snaps — base / ink / signal ease across the cut.
//   2) Fire a film-like light flare (soft bloom + horizontal streak)
//      when a chapter boundary is crossed — inspired by atmospheric
//      horizon glow and lens-flare wipes, not UI shutters.
//
// Progress is read from window.__planetProgress each tick; React state
// only updates when the integer scene index changes (for the flare).

import { useEffect, useRef, useState } from 'react';
import { SCENES } from './planetSections';
import { sceneAtProgress } from './sceneState';

const FLARE_MS = 1400;
const HANDOFF_START = 0.72; // blendIn where we begin mixing toward next
const ARRIVAL_END = 0.28; // blendIn where arrival from previous finishes
const EXHIBITION_BLACK = '#080a0f';

function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function mixHex(a: string, b: string, t: number): string {
  const [ar, ag, ab] = hexToRgb(a);
  const [br, bg, bb] = hexToRgb(b);
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  const r = Math.round(ar + (br - ar) * k);
  const g = Math.round(ag + (bg - ag) * k);
  const bl = Math.round(ab + (bb - ab) * k);
  return `#${((1 << 24) + (r << 16) + (g << 8) + bl).toString(16).slice(1)}`;
}

function smoothstep(t: number): number {
  const k = t < 0 ? 0 : t > 1 ? 1 : t;
  return k * k * (3 - 2 * k);
}

function applySceneColors(base: string, ink: string, signal: string, filter: string) {
  const root = document.documentElement;
  // The planet used to take each chapter's swatch all the way to the
  // viewport edges. Keep the gallery space almost black instead; chapter
  // color now behaves like a reflected light from the changing object.
  const exhibitionBase = mixHex(EXHIBITION_BLACK, base, 0.14);
  root.style.setProperty('--planet-scene-filter', filter);
  root.style.setProperty('--scene-base', exhibitionBase);
  root.style.setProperty('--scene-ink', '#f7f3ed');
  root.style.setProperty('--scene-tint', base);
  root.style.setProperty('--scene-signal', signal);
  root.style.setProperty('--bg', exhibitionBase);
  root.style.setProperty('--text-primary', '#f7f3ed');
  root.style.setProperty(
    '--text-secondary',
    'color-mix(in srgb, #f7f3ed 72%, transparent)',
  );
  root.style.setProperty(
    '--text-muted',
    'color-mix(in srgb, #f7f3ed 48%, transparent)',
  );
  root.style.setProperty('--accent', '#f7f3ed');
  root.style.setProperty('--accent-cool', signal);
  root.style.setProperty('--accent-link', signal);
}

export function SceneColorGrade() {
  const [flare, setFlare] = useState(false);
  const [flareKey, setFlareKey] = useState(0);
  const flareTimer = useRef<number | null>(null);
  const lastIdx = useRef(0);

  // Continuous color blend + dual-tint bookkeeping.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    let raf = 0;
    const tick = () => {
      const p =
        typeof window.__planetProgress === 'number' ? window.__planetProgress : 0;
      const { idx: cur, blendIn } = sceneAtProgress(p);
      const scene = SCENES[cur] ?? SCENES[0];
      const next = SCENES[Math.min(cur + 1, SCENES.length - 1)];
      const prev = SCENES[Math.max(cur - 1, 0)];

      let exitT = 0;
      let enterT = 0;
      if (blendIn > HANDOFF_START && cur < SCENES.length - 1) {
        exitT = smoothstep((blendIn - HANDOFF_START) / (1 - HANDOFF_START));
      } else if (blendIn < ARRIVAL_END && cur > 0) {
        enterT = smoothstep(1 - blendIn / ARRIVAL_END);
      }

      // Color field: blend with neighbor during exit or arrival.
      let base = scene.baseColor;
      let ink = scene.inkColor;
      let signal = scene.signalColor;
      if (exitT > 0) {
        base = mixHex(scene.baseColor, next.baseColor, exitT);
        ink = mixHex(scene.inkColor, next.inkColor, exitT);
        signal = mixHex(scene.signalColor, next.signalColor, exitT);
      } else if (enterT > 0) {
        base = mixHex(scene.baseColor, prev.baseColor, enterT * 0.55);
        ink = mixHex(scene.inkColor, prev.inkColor, enterT * 0.55);
        signal = mixHex(scene.signalColor, prev.signalColor, enterT * 0.55);
      }
      applySceneColors(base, ink, signal, scene.sceneFilter);

      const root = document.documentElement;
      root.style.setProperty('--scene-handoff', Math.max(exitT, enterT).toFixed(4));
      root.style.setProperty('--scene-base-next', next.baseColor);

      if (cur !== lastIdx.current) {
        lastIdx.current = cur;
        const reduced =
          window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (!reduced) {
          setFlare(true);
          setFlareKey((k) => k + 1);
          if (flareTimer.current !== null) window.clearTimeout(flareTimer.current);
          flareTimer.current = window.setTimeout(() => setFlare(false), FLARE_MS);
        }
      }

      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelAnimationFrame(raf);
      if (flareTimer.current !== null) window.clearTimeout(flareTimer.current);
    };
  }, []);

  return (
    <>
      <div
        key={flareKey}
        className={['planet-flare', flare ? 'is-active' : ''].filter(Boolean).join(' ')}
        aria-hidden="true"
      >
        <span className="planet-flare__bloom" />
        <span className="planet-flare__streak" />
        <span className="planet-flare__wash" />
      </div>
    </>
  );
}

export default SceneColorGrade;
